import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faInfoCircle, 
  faRobot, 
  faHashtag, 
  faTag, 
  faCheckCircle, 
  faCalendar,
  faCog,
  faTimes
} from '@fortawesome/free-solid-svg-icons';

const iconMapping = {
  'info-circle': faInfoCircle,
  'robot': faRobot,
  'hashtag': faHashtag,
  'tag': faTag,
  'check-circle': faCheckCircle,
  'calendar': faCalendar,
  'cog': faCog,
  'times': faTimes
};

const ModalRenderer = ({ definition, agentId, onClose, onNavigate }) => {
  const handleAction = (action, target) => {
    switch (action) {
      case 'close_modal':
        onClose();
        break;
      case 'navigate':
        if (target && onNavigate) {
          const url = target.replace('{agent_id}', agentId);
          onNavigate(url);
        }
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  const renderContent = (content) => {
    switch (content.type) {
      case 'text':
        const Tag = content.tag || 'p';
        return (
          <Tag
            key={Math.random()}
            className={content.css_class || ''}
            style={content.style ? parseStyle(content.style) : {}}
          >
            {content.content?.replace('{agent_id}', agentId)}
          </Tag>
        );

      case 'icon':
        return (
          <div
            key={Math.random()}
            className={content.css_class || ''}
            style={content.style ? parseStyle(content.style) : {}}
          >
            <FontAwesomeIcon
              icon={iconMapping[content.icon] || faInfoCircle}
            />
          </div>
        );

      case 'info_card':
        return (
          <div
            key={Math.random()}
            className={content.css_class || 'bg-gray-50 rounded-lg p-4'}
          >
            <h3 className="font-semibold text-gray-900 mb-3">{content.title}</h3>
            <div className="space-y-3">
              {content.items?.map((item, index) => (
                <div key={index} className="flex items-center">
                  {item.icon && (
                    <FontAwesomeIcon
                      icon={iconMapping[item.icon] || faInfoCircle}
                      className="text-gray-400 mr-3 w-4"
                    />
                  )}
                  <span className="text-gray-600 mr-2">{item.label}:</span>
                  <span className={item.value_class || 'text-gray-900'}>
                    {item.value?.replace('{agent_id}', agentId)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'button':
        return (
          <button
            key={Math.random()}
            className={content.css_class || 'btn'}
            style={content.style ? parseStyle(content.style) : {}}
            onClick={() => handleAction(content.action, content.target)}
          >
            {content.icon && (
              <FontAwesomeIcon
                icon={iconMapping[content.icon] || faInfoCircle}
                className="mr-2"
              />
            )}
            {content.text}
          </button>
        );

      default:
        return null;
    }
  };

  const renderSection = (section) => {
    return (
      <div
        key={Math.random()}
        className={section.css_class || ''}
        style={section.style ? parseStyle(section.style) : {}}
      >
        {section.content?.map((content, index) => (
          <div key={index}>
            {renderContent(content)}
          </div>
        ))}
      </div>
    );
  };

  const parseStyle = (styleStr) => {
    if (!styleStr) return {};
    const styles = {};
    styleStr.split(';').forEach(rule => {
      const [property, value] = rule.split(':').map(s => s.trim());
      if (property && value) {
        const camelCaseProperty = property.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
        styles[camelCaseProperty] = value;
      }
    });
    return styles;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className="bg-white rounded-lg shadow-xl max-h-[90vh] overflow-y-auto"
        style={{
          width: definition.config?.width || '500px',
          ...(definition.config?.custom_css ? parseStyle(definition.config.custom_css) : {})
        }}
      >
        {/* Close button */}
        <div className="absolute top-4 right-4 z-10">
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>

        {/* Render sections */}
        {definition.sections?.map((section, index) => (
          <div key={index}>
            {renderSection(section)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ModalRenderer;